import React from 'react';

const steps = [
  { step: '01', title: 'Start Where the Conversation Starts', description: 'A simple current-state question, headcount by department, or jump straight into plan vs actual or a growth scenario.' },
  { step: '02', title: 'Snowflake Cortex Interprets', description: 'Cortex maps each question to governed HR metrics, using the same definitions your BI team certifies, not ad-hoc logic.' },
  { step: '03', title: 'Semantic Layer Responds', description: 'Your expandable semantic model connects natural language to consistent dimensions, measures, and reporting views.' },
  { step: '04', title: 'Dashboard Activates', description: 'Agentic Power BI picks the governed report view and applies dynamic filters. Where the Snowflake result supports it, a live chart is generated beneath the embed from the same query rows.' },
  { step: '05', title: 'Follow the Thread', description: 'Leaders ask the next question, attrition in those departments, open reqs, labor cost vs plan, each turn keeps its own dashboard and Snowflake visualization context.' },
  { step: '06', title: 'Hypothesis Becomes Evidence', description: 'Assumptions collapse into governed answers fast enough to use in the meeting, current state, trends, plans, and forecasts in one session.' },
];

export function AgenticWorkflowSteps({ className = '' }) {
  return (
    <div className={className}>
      <div className="relative max-w-md lg:max-w-none text-left">
        <div className="absolute left-4 top-3 bottom-3 w-px bg-brand-200 hidden sm:block" aria-hidden="true" />
        <ol className="space-y-0">
          {steps.map((s, i) => (
            <li
              key={s.step}
              className={`relative sm:pl-12 py-5 ${i < steps.length - 1 ? 'border-b border-ink-100 sm:border-b-0' : ''}`}
            >
              <span className="hidden sm:flex absolute left-0 top-5 h-8 w-8 items-center justify-center rounded-full bg-brand-700 text-white text-xs font-semibold z-10">
                {s.step}
              </span>
              <span className="sm:hidden text-xs font-mono font-semibold text-brand-500 mb-1 block">{s.step}</span>
              <p className="text-base font-semibold text-ink-900">{s.title}</p>
              <p className="mt-1.5 text-sm text-ink-600 leading-relaxed max-w-xl">{s.description}</p>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-8 rounded-xl bg-brand-50 border border-brand-100 px-6 py-5 text-left">
        <p className="text-sm font-semibold text-brand-700 mb-1">Key Differentiator</p>
        <p className="text-base text-ink-700 leading-relaxed">
          The value is not a chatbot answer alone, it is dynamic, governed analytics
          without report fatigue. Each question drives agentic Power BI activation on
          pre-built enterprise reports, the right bookmark, the right filters, no manual
          slicer work. Where applicable, Snowflake generates a supplementary chart from
          the live query result so the ad-hoc answer is visualized immediately, not only
          in the governed PBI view. No AI-invented PBIX charts, only approved Power BI
          visualizations plus inference-based charts from governed SQL output.
        </p>
      </div>
    </div>
  );
}

export default function AgenticWorkflow() {
  return (
    <section className="py-16 md:py-20">
      <div className="container-x">
        <div className="max-w-3xl mb-10 lg:mb-12 text-left">
          <p className="eyebrow">Agentic Architecture</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">
            Analytics That Move at the Speed of Conversation
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-ink-600">
            Traditional workforce analytics stalls on ticket queues, static dashboards, and
            exhausting Power BI report libraries. Semantic HR activates the right pre-approved
            view for every question, with filters set from context, so leaders stop
            searching workspaces and start answering follow-ups.
          </p>
        </div>

        <AgenticWorkflowSteps />
      </div>
    </section>
  );
}
